// src/api/emailConfigApi.ts
import { ENV } from '../../config/runtimeEnv';
import {
	CompanyEmailConfig,
	UserEmailConfig,
	CreateCompanyEmailConfigRequest,
	CreateUserEmailConfigRequest,
	EmailConfigRequest,
	TestConfigRequest,
	EmailConfigResponse,
	EmailConfigListResponse,
	TestConfigResponse
} from '../../types/communications/emailConfig';
import type { UserUuid } from '../../types/identity';
import { createServiceClient } from '../../lib/http/client';

export const API_BASE_URL = ENV.VITE_NOTIFICATION_BASE_URL;

// Create axios instance with default config
const createApiInstance = () => {
	return createServiceClient({
		baseURL: API_BASE_URL,
		headers: {
			'Content-Type': 'application/json',
		},
	});
};

// Company Email Configuration API
export const companyEmailConfigApi = {
	// Get all configurations for a company
	getConfigurations: async (companyId: number): Promise<CompanyEmailConfig[]> => {
		try {
			const api = createApiInstance();
			const response = await api.get<EmailConfigListResponse<CompanyEmailConfig>>(
				'/company-email-configs',
				{ params: { company_id: companyId } }
			);
			return response.data.data;
		} catch (error) {
			console.error('Error fetching company email configurations:', error);
			throw error;
		}
	},

	// Create new configuration
	createConfiguration: async (config: CreateCompanyEmailConfigRequest): Promise<CompanyEmailConfig> => {
		try {
			const api = createApiInstance();
			const response = await api.post<EmailConfigResponse<CompanyEmailConfig>>(
				'/company-email-configs',
				config
			);
			if (response.data.status === 'error') {
				throw new Error(response.data.message || 'Failed to create configuration');
			}
			return response.data.data!;
		} catch (error) {
			console.error('Error creating company email configuration:', error);
			throw error;
		}
	},

	// Update existing configuration
	updateConfiguration: async (id: number, config: Partial<EmailConfigRequest>): Promise<CompanyEmailConfig> => {
		try {
			const api = createApiInstance();
			const response = await api.put<EmailConfigResponse<CompanyEmailConfig>>(
				`/company-email-configs/${id}`,
				config
			);
			if (response.data.status === 'error') {
				throw new Error(response.data.message || 'Failed to update configuration');
			}
			return response.data.data!;
		} catch (error) {
			console.error('Error updating company email configuration:', error);
			throw error;
		}
	},

	// Delete configuration
	deleteConfiguration: async (id: number): Promise<void> => {
		try {
			const api = createApiInstance();
			const response = await api.delete<EmailConfigResponse>(`/company-email-configs/${id}`);
			if (response.data.status === 'error') {
				throw new Error(response.data.message || 'Failed to delete configuration');
			}
		} catch (error) {
			console.error('Error deleting company email configuration:', error);
			throw error;
		}
	},

	// Test configuration
	testConfiguration: async (id: number, testRequest: TestConfigRequest): Promise<TestConfigResponse> => {
		try {
			const api = createApiInstance();
			const response = await api.post<EmailConfigResponse<TestConfigResponse>>(
				`/company-email-configs/${id}/test`,
				testRequest
			);
			if (response.data.status === 'error') {
				throw new Error(response.data.message || 'Test failed');
			}
			return response.data.data!;
		} catch (error) {
			console.error('Error testing company email configuration:', error);
			throw error;
		}
	},

	// Activate configuration
	activateConfiguration: async (id: number): Promise<void> => {
		try {
			const api = createApiInstance();
			const response = await api.put<EmailConfigResponse>(`/company-email-configs/${id}/activate`);
			if (response.data.status === 'error') {
				throw new Error(response.data.message || 'Failed to activate configuration');
			}
		} catch (error) {
			console.error('Error activating company email configuration:', error);
			throw error;
		}
	}
};

// User Email Configuration API
export const userEmailConfigApi = {
	// Get all configurations for a user
	getConfigurations: async (userId: UserUuid): Promise<UserEmailConfig[]> => {
		try {
			const api = createApiInstance();
			const response = await api.get<EmailConfigListResponse<UserEmailConfig>>(
				'/user-email-configs',
				{ params: { user_id: userId } }
			);
			return response.data.data;
		} catch (error) {
			console.error('Error fetching user email configurations:', error);
			throw error;
		}
	},

	// Create new configuration
	createConfiguration: async (config: CreateUserEmailConfigRequest): Promise<UserEmailConfig> => {
		try {
			const api = createApiInstance();
			const response = await api.post<EmailConfigResponse<UserEmailConfig>>(
				'/user-email-configs',
				config
			);
			if (response.data.status === 'error') {
				throw new Error(response.data.message || 'Failed to create configuration');
			}
			return response.data.data!;
		} catch (error) {
			console.error('Error creating user email configuration:', error);
			throw error;
		}
	},

	// Update existing configuration
	updateConfiguration: async (id: number, config: Partial<EmailConfigRequest>): Promise<UserEmailConfig> => {
		try {
			const api = createApiInstance();
			const response = await api.put<EmailConfigResponse<UserEmailConfig>>(
				`/user-email-configs/${id}`,
				config
			);
			if (response.data.status === 'error') {
				throw new Error(response.data.message || 'Failed to update configuration');
			}
			return response.data.data!;
		} catch (error) {
			console.error('Error updating user email configuration:', error);
			throw error;
		}
	},

	// Delete configuration
	deleteConfiguration: async (id: number): Promise<void> => {
		try {
			const api = createApiInstance();
			const response = await api.delete<EmailConfigResponse>(`/user-email-configs/${id}`);
			if (response.data.status === 'error') {
				throw new Error(response.data.message || 'Failed to delete configuration');
			}
		} catch (error) {
			console.error('Error deleting user email configuration:', error);
			throw error;
		}
	},

	// Test configuration
	testConfiguration: async (id: number, testRequest: TestConfigRequest): Promise<TestConfigResponse> => {
		try {
			const api = createApiInstance();
			const response = await api.post<EmailConfigResponse<TestConfigResponse>>(
				`/user-email-configs/${id}/test`,
				testRequest
			);
			if (response.data.status === 'error') {
				throw new Error(response.data.message || 'Test failed');
			}
			return response.data.data!;
		} catch (error) {
			console.error('Error testing user email configuration:', error);
			throw error;
		}
	},

	// Activate configuration
	activateConfiguration: async (id: number): Promise<void> => {
		try {
			const api = createApiInstance();
			const response = await api.put<EmailConfigResponse>(`/user-email-configs/${id}/activate`);
			if (response.data.status === 'error') {
				throw new Error(response.data.message || 'Failed to activate configuration');
			}
		} catch (error) {
			console.error('Error activating user email configuration:', error);
			throw error;
		}
	}
};

// Helper functions
export const validateEmailConfig = (config: Partial<EmailConfigRequest>): string[] => {
	const errors: string[] = [];

	if (!config.name?.trim()) errors.push('Configuration name is required');
	if (!config.host?.trim()) errors.push('SMTP host is required');
	if (!config.port || config.port < 1 || config.port > 65535) errors.push('Valid port number is required (1-65535)');
	if (!config.encryption || !['tls', 'ssl'].includes(config.encryption)) errors.push('Encryption must be TLS or SSL');
	if (!config.username?.trim()) errors.push('Username is required');
	if (!config.password?.trim()) errors.push('Password is required');
	if (!config.from_address?.trim()) errors.push('From address is required');
	if (!config.from_name?.trim()) errors.push('From name is required');

	// Email format validation
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	if (config.from_address && !emailRegex.test(config.from_address)) {
		errors.push('Invalid email address format');
	}

	return errors;
};

export const getActiveConfiguration = (configs: (CompanyEmailConfig | UserEmailConfig)[]): CompanyEmailConfig | UserEmailConfig | null => {
	return configs.find(config => config.is_active && config.is_verified) || null;
};